import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { useOpenSolarSync } from '@/hooks/use-opensolar-sync';
import { EnhancedSdkView } from '@/components/opensolar/enhanced-sdk-view';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  BarChart3,
  Cloud,
  CloudRain,
  Database,
  Info,
  Layers,
  Maximize2,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';

export default function OpenSolarDesignViewPage() {
  const { toast } = useToast();
  const { projects, isLoadingProjects, triggerSync, isSyncing } = useOpenSolarSync();
  const [activeProject, setActiveProject] = useState<string | null>(null);
  const [designData, setDesignData] = useState<any | null>(null);
  const [isIdle, setIsIdle] = useState(false);

  // Use the first project as active project when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && !activeProject) {
      setActiveProject(projects[0].id);
    }
  }, [projects, activeProject]);

  // Handle design data fetched from the SDK
  const handleDesignDataFetched = (data: any) => {
    setDesignData(data);

    toast({
      title: 'Design Data Fetched',
      description: 'Successfully retrieved design data from OpenSolar',
      variant: 'default',
    });
  };

  // Get the active project from the projects list
  const getActiveProject = () => {
    return projects.find((project) => project.id === activeProject);
  };

  // Extract key metrics from design data
  const getMetrics = () => {
    const metrics = {
      systemSize: 'N/A',
      panelCount: 'N/A',
      annualOutput: 'N/A',
      co2Offset: 'N/A',
    };

    if (
      designData &&
      designData.object &&
      designData.object.children &&
      designData.object.children.length > 0
    ) {
      const systemData = designData.object.children[0]?.userData;

      if (systemData) {
        if (systemData.moduleQuantity) {
          metrics.panelCount = systemData.moduleQuantity.toString();
        }

        if (systemData.output && systemData.output.annual) {
          const annualKWh = Math.round(systemData.output.annual);
          metrics.annualOutput = `${annualKWh} kWh`;

          // Rough calculation of CO2 offset (0.4 kg CO2 per kWh)
          const co2Offset = Math.round(annualKWh * 0.4);
          metrics.co2Offset = `${co2Offset} kg`;
        }

        if (systemData.system_size) {
          metrics.systemSize = `${systemData.system_size} kW`;
        }
      }
    }

    return metrics;
  };

  const metrics = getMetrics();
  const project = getActiveProject();

  return (
    <div className="container py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Link href="/opensolar/projects">
            <Button variant="ghost" size="sm" className="flex items-center gap-2 mb-2">
              <ArrowLeft className="h-4 w-4" /> Back to Projects
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">OpenSolar Design View</h1>
          <p className="text-muted-foreground mt-1">View and analyze 3D solar designs</p>
        </div>

        <Button
          onClick={() => triggerSync()}
          disabled={isSyncing}
          variant="outline"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing...' : 'Sync Projects'}
        </Button>
      </div>

      {isLoadingProjects ? (
        <div className="space-y-4">
          <Skeleton className="h-[600px] w-full rounded-md" />
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No Projects Available</CardTitle>
            <CardDescription>
              There are no OpenSolar projects available. Please ensure you have configured the
              OpenSolar API correctly and have projects in your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Database className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No OpenSolar Projects Found</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                You need to create projects in OpenSolar before they can be viewed here. Make sure
                your API key is configured correctly.
              </p>
              <Link href="/opensolar/settings">
                <Button variant="secondary">Configure OpenSolar</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div>
          {project && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl font-semibold">{project.name}</h2>
                <Badge variant="outline" className="capitalize">
                  {project.status}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{project.address}</p>
            </div>
          )}

          <Tabs defaultValue="design" className="space-y-4">
            <TabsList>
              <TabsTrigger value="design" className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                3D Design View
              </TabsTrigger>
              <TabsTrigger value="data" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                System Data
              </TabsTrigger>
              <TabsTrigger value="project" className="flex items-center gap-2">
                <Info className="h-4 w-4" />
                Project Details
              </TabsTrigger>
            </TabsList>

            <TabsContent value="design" className="space-y-4">
              <div className="relative">
                <EnhancedSdkView
                  projectId={activeProject}
                  height={600}
                  showIdleStatus={true}
                  onIdle={setIsIdle}
                  onDesignDataFetched={handleDesignDataFetched}
                />
              </div>
            </TabsContent>

            <TabsContent value="data" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex justify-between items-center">
                      System Size
                      <Maximize2 className="h-4 w-4 text-muted-foreground" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{metrics.systemSize}</div>
                    <p className="text-sm text-muted-foreground mt-1">Total system capacity</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex justify-between items-center">
                      Panel Count
                      <Layers className="h-4 w-4 text-muted-foreground" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{metrics.panelCount}</div>
                    <p className="text-sm text-muted-foreground mt-1">Number of solar panels</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex justify-between items-center">
                      Annual Output
                      <Zap className="h-4 w-4 text-muted-foreground" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{metrics.annualOutput}</div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Estimated yearly production
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex justify-between items-center">
                      CO₂ Offset
                      <Cloud className="h-4 w-4 text-muted-foreground" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{metrics.co2Offset}</div>
                    <p className="text-sm text-muted-foreground mt-1">Annual carbon reduction</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Generation Profile</CardTitle>
                  <CardDescription>
                    Monthly electricity generation estimates based on historical weather data
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {designData ? (
                    <div className="h-64 flex items-center justify-center">
                      <p className="text-muted-foreground">
                        Energy generation data available in the full OpenSolar integration
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 space-y-4">
                      <CloudRain className="h-10 w-10 text-muted-foreground" />
                      <p className="text-muted-foreground text-center">
                        No generation data available yet.
                        <br />
                        Wait for the design to fully load and become idle.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="project" className="space-y-4">
              {project && (
                <Card>
                  <CardHeader>
                    <CardTitle>Project Information</CardTitle>
                    <CardDescription>
                      Detailed information about this OpenSolar project
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                          Project Name
                        </h3>
                        <p>{project.name}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                          Project ID
                        </h3>
                        <p className="font-mono text-xs">{project.id}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                        <p className="capitalize">{project.status}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Customer</h3>
                        <p>{project.customerName || 'Not specified'}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Address</h3>
                        <p>{project.address || 'Not specified'}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                          System Type
                        </h3>
                        <p>{project.systemType || 'Not specified'}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Created</h3>
                        <p>
                          {project.createdAt
                            ? new Date(project.createdAt).toLocaleDateString()
                            : 'Unknown'}
                        </p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                          Last Updated
                        </h3>
                        <p>
                          {project.updatedAt
                            ? new Date(project.updatedAt).toLocaleDateString()
                            : 'Unknown'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
